import { axiosClient as axios } from './axiosClient';

import { api } from '@config/apiEndpoints';
import {
  failureResult,
  ResultViewModel,
  successResult,
} from '@models/common/ResultViewModel';
import { CustomerSelectOptionResultViewModel } from '@models/Customer';
import {
  InvoiceResultViewModel,
  InvoiceTableResultViewModel,
  InvoiceViewModel,
} from '@models/Invoice';
import { InvoiceDetailListResultViewModel } from '@models/InvoiceDetail';
import { handleAxiosError } from '@utils/axiosErrorHandler';
import { getDataUrl } from '@utils/dataUrls';

export const InvoiceService = {
  create: async function (
    invoice: InvoiceViewModel
  ): Promise<InvoiceResultViewModel | ResultViewModel> {
    const createUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}`;

    try {
      const response = await axios.post(createUrl, invoice);
      return response.data as InvoiceResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  getList: async function (
    page?: number,
    pageSize?: number,
    searchFields?: string,
    search?: string,
    filters?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<InvoiceTableResultViewModel | ResultViewModel> {
    let dataUrl = getDataUrl(
      api.BASE_URL,
      api.INVOICES_ENDPOINT,
      page,
      pageSize,
      searchFields,
      search,
      sortBy,
      sortDirection
    );

    if (filters) {
      dataUrl = dataUrl + filters;
    }

    try {
      const response = await axios.get(dataUrl);
      return response.data as InvoiceTableResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  getById: async function (
    id: number
  ): Promise<InvoiceResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as InvoiceResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  delete: async function (id: number): Promise<ResultViewModel> {
    const deleteURL = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}`;

    try {
      const response = await axios.delete(deleteURL);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  update: async function (
    id: number,
    estimates: InvoiceViewModel
  ): Promise<ResultViewModel> {
    const updateUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}`;

    try {
      const response = await axios.put(updateUrl, estimates);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  sendEmail: async function (
    id: number,
    file?: File
  ): Promise<ResultViewModel> {
    const sendEmailUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}/send-email`;
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }

    try {
      const response = await axios.post(sendEmailUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  duplicate: async function (id: number): Promise<ResultViewModel> {
    const duplicateUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}/duplicate`;

    try {
      const response = await axios.post(duplicateUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  post: async function (id: number): Promise<ResultViewModel> {
    const postUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}/post`;

    try {
      const response = await axios.post(postUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  posted: async function (id: number): Promise<ResultViewModel> {
    const postedUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}/posted`;

    try {
      const response = await axios.post(postedUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  void: async function (id: number): Promise<ResultViewModel> {
    const voidUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}/void`;

    try {
      const response = await axios.post(voidUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getInvoiceDetails: async function (
    id: number
  ): Promise<InvoiceDetailListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/${id}/details`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as InvoiceDetailListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerFilterSelectOptions: async function (): Promise<
    CustomerSelectOptionResultViewModel | ResultViewModel
  > {
    const dataUrl = `${api.BASE_URL}${api.INVOICES_ENDPOINT}/customer-filter-select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
};
